import { useState, useEffect } from "react";
import { SquarePlus } from "lucide-react";
import TestCard from "./testscores/TestCard";
import TestScoreModal from "./testscores/TestScoreModal";
import SuccessDialog from "./common/SuccessDialog";
import ConfirmDialog from "./common/ConfirmDialog";
import { updateTestScore, saveTestScore } from "../api/TestscoreAPI";
import { Snackbar, Alert } from "@mui/material";
import { authFetch } from "../../api";

const TestScores = ({ testScoreData: initialData, onSaveSuccess }) => {
  const [testData, setTestData] = useState(initialData || []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditData, setCurrentEditData] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    data: null,
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const user_id = localStorage.getItem("userId");
  useEffect(() => {
    setTestData(initialData || []);
  }, [initialData]);

  /** ------------------------
   * Save Test Score (Add)
   * ----------------------- */
  const handleAddSave = async (formData) => {
    setConfirmDialog({ open: true, data: { mode: "add", formData } });
  };

  /** ------------------------
   * Edit Test Score (Update)
   * ----------------------- */
  const handleEditSave = async (formData, id) => {
    setConfirmDialog({ open: true, data: { mode: "edit", formData, id } });
  };

  /** ------------------------
   * Confirm Save -> Call API
   * ----------------------- */
  const handleConfirm = async () => {
    const { mode, formData, id } = confirmDialog.data || {};
    try {
      let res;
      if (mode === "edit") {
        res = await updateTestScore({ ...formData, id });
      } else {
        let payload = {
          ...formData,
          user_id: user_id,
        };
        res = await saveTestScore(payload);
      }

      if (res?.success) {
        setConfirmDialog({ open: false, data: null });
        setShowEditModal(false);
        setShowAddModal(false);
        setShowSuccess(true);
      } else {
        setSnackbar({
          open: true,
          message: res?.message || "Failed to save test score",
        });
      }
    } catch (err) {
      console.error("Error saving test score:", err);
      setSnackbar({ open: true, message: "Something went wrong. Try again." });
    }
  };

  /** ------------------------
   * Open Edit
   * ----------------------- */
  const handleCardEdit = (test) => {
    setCurrentEditData(test);
    setShowEditModal(true);
  };

  const handleCardDelete = async (testId) => {
    try {
      const response = await authFetch("profile/delete-test-scores", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: testId }),
      });
      // const data = await response.json();
    } catch (err) {
      console.error("Delete error:", err);
    }
    setTestData((prevData) => prevData.filter((test) => test.id !== testId));
    window.location.reload();
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div className="text-2xl font-semibold font-inter text-[#572575] ">
          Test Score Details{" "}
        </div>
        <button
          className="text-sm text-white bg-[#9D0385] px-4 py-2 rounded-xl font-inter"
          onClick={() => setShowAddModal(true)}
        >
          <span className="flex gap-2">
            <SquarePlus /> Add Test Score
          </span>
        </button>
      </div>

      {testData.map((test) => (
        <TestCard
          key={test.id}
          testScore={test}
          onEdit={() => handleCardEdit(test)}
          onDelete={() => handleCardDelete(test.id)}
        />
      ))}

      {/* Add Modal */}
      <TestScoreModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddSave}
        existingExamIds={testData.map((t) => t.exam_id)}
      />

      {/* Edit Modal */}
      <TestScoreModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setCurrentEditData(null);
        }}
        onSave={handleEditSave}
        editData={currentEditData}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title="Confirm Save"
        message="Are you sure you want to save this test score?"
        onCancel={() => setConfirmDialog({ open: false, data: null })}
        onConfirm={handleConfirm}
      />

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccess}
        message="Your test score has been saved successfully."
        onClose={() => setShowSuccess(false)}
        onSuccess={() => {
          setShowSuccess(false);
          if (onSaveSuccess) onSaveSuccess();
        }}
      />

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TestScores;
